import {
  Box,
  Circle,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { FC, MouseEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";

import { supportStore, openSupportDrawer } from "../../utils/support";
import Icon from "../../components/Icon";
import NotificationButton from "../../components/NotificationButton";
import { userStore } from "../../hooks/store";
import { Routes } from "../../routes/baseRoutes";
import { UserWeddingRole } from "../../types/wedding";
import { ClientStorage, ClientStorageItem } from "../../utils/clientstorage";
import { editorState, WebsiteStore, WebsiteView } from "../../utils/editor";
import { isValidObjectID } from "../../utils/glob";
import {
  websiteSections,
  WebsiteSectionsProps,
} from "../../utils/website.sections";
import ImageMenu from "./components/ImageMenu";
import LinkTabWebsiteChain from "./components/LinkTabWebsiteChain";
import { ClipboardCheck, Gem, Headset } from "lucide-react";
import { useEventPlannerPath } from "../../hooks/useEventPlannerPath";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { getCoupleDisplayName } from "../../utils/couple.names";

type RouteTabProps = {
  url?: string;
  isActive?: boolean;
  tabName: string;
  showNotifications?: boolean;
  showDropdown?: boolean; // New prop to toggle the dropdown
  dropdownOptions?: WebsiteSectionsProps[];
};

export const RouteTab: FC<RouteTabProps> = ({
  url,
  isActive,
  tabName,
  showNotifications,
  showDropdown,
  dropdownOptions,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile] = useMediaQuery("(max-width: 1070px)");

  const navigate = useNavigate();

  const handleClick = (e: MouseEvent) => {
    if (showDropdown) {
      e.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    } else {
      navigate(url as string);
    }
  };

  const handleDropDownClick = (subUrl: string) => () => {
    navigate(subUrl);
  };

  return (
    <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
      <Box
        position="relative"
        onClick={handleClick}
        onMouseEnter={() => setIsMenuOpen(true)}
        onMouseLeave={() => setIsMenuOpen(false)}
      >
        <MenuButton display="block" _hover={{ textDecoration: "none" }}>
          <Box
            borderRadius="48px"
            gap={2}
            py={"0.5rem"}
            px={{ base: "0.5rem", md: "1rem" }}
            bgColor={isActive ? "#0F1D14" : "#FFFFFF"}
            border={"1px solid #DFE6ED"}
            display="flex"
            alignItems="center"
          >
            <Text
              fontFamily={"font4"}
              fontWeight={isActive ? 600 : 500}
              color={isActive ? "#FFFFFF" : "#000000"}
              fontSize={"0.8rem"}
              letterSpacing={"1.25px"}
              textTransform={"uppercase"}
            >
              {isMobile && tabName === "Edit Website" ? "Edit" : tabName}
            </Text>
            {showDropdown && (
              <Circle
                size={5}
                ml={1}
                border={`1px solid ${isActive ? "#DFE6ED" : "#00000061"}`}
              >
                <Icon
                  name="arrowDown"
                  variant="stroke"
                  boxSize={4}
                  color={isActive ? "#DFE6ED" : "#00000061"}
                />
              </Circle>
            )}
          </Box>
        </MenuButton>
        {showDropdown && (
          <MenuList fontFamily={"font4"}>
            {dropdownOptions?.map((section, index) => (
              <MenuItem
                key={"menuitem" + index}
                onClick={handleDropDownClick(section.url)}
                isDisabled={section?.isDisabled}
              >
                {section.title}
                {section.comingSoon && (
                  <Text fontSize={"10px"} color={"orange"}>
                    COMING SOON
                  </Text>
                )}
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Box>
      {showNotifications && (
        <Box display={"flex"} justifyContent={"center"} mt={"-7px"}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="4"
            height="4"
            viewBox="0 0 4 4"
            fill="none"
          >
            <circle cx="2" cy="2" r="2" fill="#FF3939" />
          </svg>
        </Box>
      )}
    </Menu>
  );
};

type LinkTabProps = {
  url?: string;
  isActive: boolean;
  tabName: string;
  showNotifications?: boolean;
  displayLeftIcon?: boolean;
  hasDropDown?: boolean;
};

const LinkTab: FC<LinkTabProps> = ({
  url,
  isActive,
  tabName,
  showNotifications,
  displayLeftIcon,
  hasDropDown,
}) => {
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleClick = (e: MouseEvent) => {
    if (hasDropDown) {
      e.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    } else {
      navigate(url as string);
    }
  };

  const handleDropDownClick = (subUrl: string) => () => {
    navigate(subUrl as string);
  };

  return (
    <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
      <MenuButton
        display="block"
        _hover={{ textDecoration: "none" }}
        onClick={handleClick}
      >
        <Flex
          align={"center"}
          gap={2}
          py={"0.5rem"}
          borderBottom={isActive ? "2px solid" : "none"}
        >
          {displayLeftIcon && <LinkTabWebsiteChain />}
          <Text
            fontFamily={"font4"}
            fontWeight={400}
            color={"#000"}
            fontSize={{ base: "14px", md: "16px" }}
          >
            {tabName}
          </Text>
          {hasDropDown && (
            <Icon name="arrowDown" variant="stroke" boxSize={4} />
          )}
        </Flex>
      </MenuButton>
      {hasDropDown && (
        <MenuList fontFamily={"font4"}>
          <MenuItem onClick={handleDropDownClick("/website/edit/basics")}>
            Basics
          </MenuItem>
          <MenuItem onClick={handleDropDownClick("/website/edit/our-story")}>
            Our Story
          </MenuItem>
          <MenuItem isDisabled display="flex" justifyContent={"space-between"}>
            Accommodation{" "}
            <Text fontSize={"10px"} color={"orange"}>
              COMING SOON
            </Text>
          </MenuItem>
        </MenuList>
      )}
      {showNotifications && (
        <Box display={"flex"} justifyContent={"center"} mt={"-7px"}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="4"
            height="4"
            viewBox="0 0 4 4"
            fill="none"
          >
            <circle cx="2" cy="2" r="2" fill="#FF3939" />
          </svg>
        </Box>
      )}
    </Menu>
  );
};

const TopNav = () => {
  const { enabled: designStudioEnabled } = useFeatureFlag("design_studio");
  const location = useLocation();
  const navigate = useNavigate();
  const { websiteView } = useSnapshot(WebsiteStore);
  const [isSmallerThan900] = useMediaQuery("(max-width: 900px)");
  const [isMobile] = useMediaQuery("(max-width: 1070px)");
  const {
    data: { businessName },
  } = useSnapshot(userStore);

  const getCurrentDate = () => {
    const now = new Date();
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[now.getMonth()];
    const day = now.getDate();
    const year = now.getFullYear();
    return `${month}, ${day}, ${year}`;
  };

  const getInitials = (text: string) => {
    if (!text) return "";
    const parts = text.split("&").map((part) => part.trim());
    if (parts.length === 2) {
      const firstInitial = parts[0].charAt(0).toUpperCase();
      const secondInitial = parts[1].charAt(0).toUpperCase();
      return `${firstInitial} & ${secondInitial}`;
    }
    return text;
  };

  const isGuestFlow = location.pathname.includes("/guests");
  const isGuestMessaging = location.pathname.includes("/guests/messaging");
  const isGuestAccessCard = location.pathname.includes("/guests/accesscards");
  const isNewAccessCard = location.pathname.includes("/accesscards");
  const isWebsiteFlow = location.pathname.includes("/website");
  const isDesignsFlow = location.pathname.includes("/design");
  const isWebsiteEditFlow = location.pathname.includes("/website/edit/");
  const { isEventPlanner, basePath, userWeddingRole, weddingID: plannerWeddingID } = useEventPlannerPath();
  const inEventPlannerViewOfCouplesWedding =
    location.pathname.startsWith("/planner/weddings/");

  const isEventPlannerPath = location.pathname.startsWith("/planner/weddings/");

  const weddingIDFromURL = isEventPlannerPath ? plannerWeddingID : "";
  const validWeddingID = isValidObjectID(weddingIDFromURL);
  const isWeddingDashboard =
    !isEventPlannerPath && userWeddingRole !== UserWeddingRole.EventPlanner
      ? location.pathname === "/"
      : validWeddingID;

  const isCateringFlow = location.pathname.includes("/catering");

  const snap = useSnapshot(editorState);
  const imageInfoText = getCoupleDisplayName(snap);

  const supportSnap = useSnapshot(supportStore);

  const handleSupportClick = () => {
    openSupportDrawer();
  };

  const handleSubscriptionClick = () => {
    navigate(
      userWeddingRole === UserWeddingRole.EventPlanner
        ? Routes.weddnProSubscription
        : Routes.myWeddn,
    );
  };

  const shouldDisplayImageInfo = location.pathname.includes("/planner/weddings/");

  const coupleName = ClientStorage.fetch(ClientStorageItem.coupleName);

  const DefaultNavigationCtx = (
    <Flex
      gap={{ base: "1.5rem", sm: "2rem", md: "4rem" }}
      justifyContent={"center"}
      display={
        isSmallerThan900 &&
        !(
          location.pathname === "/website" &&
          websiteView === WebsiteView.Preview
        )
          ? "flex"
          : "none"
      }
      bgColor={"#FFFFFF"}
      color="#000"
    >
      <LinkTab
        url={basePath + "/"}
        isActive={location.pathname === "/"}
        tabName="Dashboard"
      />
      <LinkTab
        url={basePath + "/guests"}
        isActive={location.pathname === "/guests"}
        tabName="Guests"
        // showNotifications
      />
      <LinkTab
        url={basePath + "/website"}
        isActive={location.pathname === "/website"}
        tabName="Website"
        hasDropDown
      />
      {designStudioEnabled && (
        <LinkTab
          url={basePath + Routes.designs}
          isActive={location.pathname === basePath + Routes.designs}
          tabName="Designs"
        />
      )}
    </Flex>
  );

  return (
    <Flex
      w={"full"}
      h={"72px"}
      maxH={"72px"}
      flexDirection={"column"}
      align={"center"}
      position={"fixed"}
      zIndex={99}
      bg={isCateringFlow ? "#ECECEC" : "#F9F9F9"}
      {...(isWebsiteEditFlow || isGuestMessaging || isGuestAccessCard
        ? { borderBottom: "1px solid #E2E8F0" }
        : isEventPlanner
        ? {
            bg: "none",
            pt: "0.35rem",
          }
        : {})}
    >
      <Flex
        w={isEventPlanner ? "calc(100% - 0.3rem)" : "full"}
        h={"full"}
        maxW={"1440px"}
        zIndex={10}
        m={"0 auto"}
        px={{
          base: "0.8rem",
          md: isEventPlanner ? "1.56rem" : "3rem",
          lg: isEventPlanner ? "1.5rem" : "2rem",
        }}
        py={{ base: "0rem", md: "0rem" }}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={{ base: 3, md: 3, lg: 0 }}
        position={"relative"}
      >
        <Flex align={"center"}>
          <Flex align={"center"} h="36px" w="36px">
            <Link to={"/"}>
              <Image
                src={"/icons/icon-512x512.png"}
                alt="App Logo"
                h="36px"
                w="36px"
                borderRadius="full"
              />
            </Link>
          </Flex>

          <Flex
            display={isSmallerThan900 ? "none" : "flex"}
            align={"center"}
            flexDir={"row"}
            h={"46px"}
            px={"0.275rem"}
            ml={"0.875rem"}
            fontSize={"0.8rem"}
          >
            <Link to={"/"}>
              <Text
                textTransform={"uppercase"}
                letterSpacing={"1px"}
                fontWeight={500}
                color="black"
              >
                {inEventPlannerViewOfCouplesWedding ? businessName : ""}
              </Text>
            </Link>
            {location.pathname !== "/" &&
              location.pathname !== "/settings" &&
              location.pathname !== "/weddn-list" &&
              location.pathname !== "/settings/share-feedback" && (
                <Link to={basePath}>
                  <Box
                    ml={"0.425rem"}
                    pl={"0.425rem"}
                    borderLeft={"1px solid #adadad"}
                  >
                    <Text
                      textTransform={"uppercase"}
                      letterSpacing={"1px"}
                      fontWeight={500}
                      color="black"
                    >
                      {userWeddingRole === UserWeddingRole.EventPlanner
                        ? getCurrentDate()
                        : shouldDisplayImageInfo
                        ? isMobile
                          ? getInitials(coupleName)
                          : coupleName
                        : isMobile
                        ? getInitials(imageInfoText)
                        : imageInfoText}
                    </Text>
                  </Box>
                </Link>
              )}
            {isWebsiteFlow && (
              <Link to={basePath + "/website"}>
                <Box
                  ml={"0.425rem"}
                  pl={"0.425rem"}
                  borderLeft={"1px solid #adadad"}
                >
                  <Text
                    textTransform={"uppercase"}
                    letterSpacing={"1px"}
                    fontWeight={700}
                    opacity={0.5}
                  >
                    Website
                  </Text>
                </Box>
              </Link>
            )}
            {isDesignsFlow && (
              <Link to={basePath + Routes.designs}>
                <Box
                  ml={"0.425rem"}
                  pl={"0.425rem"}
                  borderLeft={"1px solid #adadad"}
                >
                  <Text
                    textTransform={"uppercase"}
                    letterSpacing={"1px"}
                    fontWeight={700}
                    opacity={0.5}
                  >
                    Designs
                  </Text>
                </Box>
              </Link>
            )}
            {isCateringFlow && (
              <Link to={basePath + "/catering"}>
                <Box
                  ml={"0.425rem"}
                  pl={"0.425rem"}
                  borderLeft={"1px solid #adadad"}
                >
                  <Text
                    textTransform={"uppercase"}
                    letterSpacing={"1px"}
                    fontWeight={700}
                    opacity={0.5}
                  >
                    Catering
                  </Text>
                </Box>
              </Link>
            )}
            {isGuestFlow && (
              <Link to={basePath + "/guests"}>
                <Box
                  ml={"0.425rem"}
                  pl={"0.425rem"}
                  borderLeft={"1px solid #adadad"}
                >
                  <Text
                    textTransform={"uppercase"}
                    letterSpacing={"1px"}
                    fontWeight={700}
                    opacity={0.5}
                  >
                    Guests
                  </Text>
                </Box>
              </Link>
            )}
            {isNewAccessCard && (
              <Link to={isEventPlanner ? `${basePath}/guests/accesscards` : Routes.accessCard}>
                <Box
                  ml={"0.425rem"}
                  pl={"0.425rem"}
                  borderLeft={"1px solid #adadad"}
                >
                  <Text
                    textTransform={"uppercase"}
                    letterSpacing={"1px"}
                    fontWeight={700}
                    opacity={0.5}
                  >
                    Access Cards
                  </Text>
                </Box>
              </Link>
            )}
          </Flex>
        </Flex>

        {/* Center section - Couple's name */}
        {isWeddingDashboard && !isSmallerThan900 && (
          <Flex
            position={"absolute"}
            left={"50%"}
            transform={"translateX(-50%)"}
            alignItems={"center"}
            justifyContent={"center"}
            className="snell"
          >
            <Text
              fontSize={"54px"}
              fontWeight={500}
              textTransform={"capitalize"}
              letterSpacing={"1.5px"}
            >
              {userWeddingRole === UserWeddingRole.EventPlanner &&
              !shouldDisplayImageInfo &&
              !imageInfoText
                ? getCurrentDate()
                : shouldDisplayImageInfo
                ? coupleName
                : imageInfoText}
              {/*{imageInfoText}*/}
            </Text>
          </Flex>
        )}

        <Flex
          alignItems={"center"}
          gap={{ base: 3, md: 4 }}
          pl={{ base: "0.6rem", md: 0 }}
        >
          {location.pathname.includes("/website") && (
            <>
              <RouteTab
                url={basePath + "/website"}
                isActive={location.pathname === "/website"}
                tabName="Overview"
              />
              <RouteTab
                url={basePath + Routes.websiteDesign}
                isActive={location.pathname === Routes.websiteDesign}
                tabName="Design"
              />
              <RouteTab
                showDropdown
                isActive={location.pathname.includes("/website/edit")}
                tabName="Edit Website"
                dropdownOptions={websiteSections(basePath)}
              />
            </>
          )}
          <Box
            position="relative"
            cursor="pointer"
            onClick={handleSupportClick}
            _hover={{ opacity: 0.7 }}
            transition="opacity 0.2s"
          >
            <Headset size={20} strokeWidth={0.8} />
            {supportSnap.unreadCount > 0 && (
              <Box
                position="absolute"
                top="-6px"
                right="-6px"
                minW="16px"
                h="16px"
                px="4px"
                bg="red.500"
                color="white"
                borderRadius="full"
                fontSize="10px"
                lineHeight="16px"
                textAlign="center"
                fontWeight="bold"
              >
                {supportSnap.unreadCount > 9 ? "9+" : supportSnap.unreadCount}
              </Box>
            )}
          </Box>

          {/* Couple checklist / planner subscription shortcut */}
          <Box
            cursor="pointer"
            onClick={handleSubscriptionClick}
            _hover={{ opacity: 0.7 }}
            transition="opacity 0.2s"
            aria-label={
              userWeddingRole === UserWeddingRole.EventPlanner
                ? "WeddnPro"
                : "My Weddn"
            }
          >
            {userWeddingRole === UserWeddingRole.EventPlanner ? (
              <Gem size={20} strokeWidth={0.8} />
            ) : (
              <ClipboardCheck size={20} strokeWidth={0.8} />
            )}
          </Box>

          {/* Notification icon with count */}
          <NotificationButton
            size="md"
            buttonClassName="hover:opacity-70 transition-opacity bg-transparent hover:bg-transparent"
            iconClassName="w-5 h-5"
            strokeWidth={0.8}
          />
          <ImageMenu settingsRoute={basePath + Routes.settings} />
        </Flex>
      </Flex>

      {/* side menu options to mobile menu  */}

      {isSmallerThan900 &&
        ((location.pathname.endsWith("/website") ||
          location.pathname.endsWith("/website/pre")) &&
        websiteView === WebsiteView.Content
          ? null
          : location.pathname.includes("/settings")
          ? null
          : DefaultNavigationCtx)}
    </Flex>
  );
};

export default TopNav;
